import React, { useState } from "react";

import { Box, Button, Chip, TextField, Typography } from "@mui/material";

import { FieldSchema } from "../../types";

interface ArrayInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

const parseScalar = (
  raw: string,
  type: FieldSchema["type"],
): string | number | boolean => {
  if (type === "boolean") {
    if (raw === "true") return true;
    if (raw === "false") return false;
  }
  if (
    type === "number" ||
    type === "int32" ||
    type === "uint32" ||
    type === "float"
  ) {
    const num = Number(raw);
    if (!isNaN(num) && raw.trim() !== "") return num;
  }
  return raw;
};

export const ArrayInput: React.FC<ArrayInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const [inputValue, setInputValue] = useState("");
  const items: Array<string | number | boolean> = (() => {
    if (Array.isArray(value)) return value as Array<string | number | boolean>;
    if (typeof value === "string" && value) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  })();

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onChange([...items, parseScalar(trimmed, field.type)]);
    setInputValue("");
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "600",
          mb: 1,
          color: "text.primary",
          fontSize: "0.9rem",
        }}
      >
        {field.name}
        {"\u00A0"}(Array)
        {!field.isOptional && " *"}
      </Typography>

      {field.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block", fontSize: "0.8rem", lineHeight: 1.4 }}
        >
          {field.description}
        </Typography>
      )}

      {items.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {items.map((item, index) => (
            <Chip
              key={index}
              label={String(item)}
              size="small"
              onDelete={() => handleRemove(index)}
              sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          size="small"
          name={field.name}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add value, then press Enter or Add"
          error={!!error && touched}
          helperText={error && touched ? error : undefined}
          sx={{
            flex: 1,
            "& .MuiFormHelperText-root": { margin: "4px 0 0 0" },
          }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};
